/* Draft motion enhancements. Native links and purchase forms remain unchanged. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class MidtfBenefitMarquee extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const button = this.querySelector('[data-motion-toggle]');
      if (!button) return;
      this.dataset.ready = 'true';
      button.hidden = false;
      button.addEventListener('click', () => {
        const paused = this.dataset.paused !== 'true';
        this.dataset.paused = String(paused);
        button.setAttribute('aria-label', paused ? 'Reanudar destacados' : 'Pausar destacados');
        button.dataset.paused = String(paused);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class MidtfProductCarousel extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.rail = this.querySelector('.midtf-products__rail');
      this.cards = [...this.querySelectorAll('.midtf-product-card')];
      this.controls = this.querySelector('.midtf-carousel-controls');
      if (!this.rail || !this.controls || !this.cards.length) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.paused = false;
      this.hovered = false;
      this.inView = false;
      this.toggle = this.querySelector('[data-motion-toggle]');
      this.count = this.querySelector('.midtf-carousel-count');
      this.status = this.querySelector('[data-carousel-status]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(4000, Number(this.dataset.interval) || 6000);
      this.querySelector('[data-carousel-prev]').addEventListener('click', () => this.advance(-1, true), options);
      this.querySelector('[data-carousel-next]').addEventListener('click', () => this.advance(1, true), options);
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.toggle.dataset.paused = String(this.paused);
        this.toggle.setAttribute('aria-label', this.paused ? 'Activar avance automático' : 'Pausar avance automático');
        this.schedule();
      }, options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hovered = true;
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusout', () => this.schedule(), options);
      this.rail.addEventListener('pointerdown', () => { this.dragging = true; this.schedule(); }, options);
      const release = () => { this.dragging = false; this.schedule(); };
      window.addEventListener('pointerup', release, options);
      window.addEventListener('pointercancel', release, options);
      this.rail.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      reducedMotion.addEventListener('change', () => { this.update(); this.schedule(); }, options);
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.rail);
      this.visibility = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.schedule();
      }, { threshold: 0.25 });
      this.visibility.observe(this.rail);
      this.update();
      this.schedule();
    }

    update() {
      const overflow = this.rail.scrollWidth > this.rail.clientWidth + 2;
      this.controls.hidden = !overflow;
      if (this.toggle) this.toggle.hidden = reducedMotion.matches;
      const viewport = this.rail.getBoundingClientRect();
      const visible = this.cards.map((card, index) => ({ box: card.getBoundingClientRect(), index }))
        .filter(({ box }) => Math.min(box.right, viewport.right) - Math.max(box.left, viewport.left) > box.width / 2);
      if (visible.length) this.count.textContent = `${visible[0].index + 1}–${visible.at(-1).index + 1} / ${this.cards.length}`;
    }

    advance(direction, announce = false) {
      const railLeft = this.rail.getBoundingClientRect().left;
      const positions = this.cards.map(card => card.getBoundingClientRect().left - railLeft + this.rail.scrollLeft);
      const current = this.rail.scrollLeft;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      let target;
      let wrap = false;
      if (direction > 0) {
        target = positions.find(position => position > current + 4);
        if (current >= max - 4 || target === undefined) { target = 0; wrap = true; }
      } else {
        target = [...positions].reverse().find(position => position < current - 4);
        if (current <= 4 || target === undefined) { target = max; wrap = true; }
      }
      this.rail.scrollTo({ left: Math.min(max, Math.max(0, target)), behavior: reducedMotion.matches || wrap ? 'instant' : 'smooth' });
      if (announce) {
        const index = positions.findIndex(position => position >= Math.min(max, target) - 4);
        this.status.textContent = `Mostrando productos desde el ${Math.max(1, index + 1)} de ${this.cards.length}`;
        this.schedule();
      }
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.paused || reducedMotion.matches || document.hidden || !this.isConnected) return;
      this.timer = setTimeout(() => {
        if (this.inView && !this.hovered && !this.contains(document.activeElement) && !this.dragging && !document.hidden && !this.controls.hidden) this.advance(1);
        this.schedule();
      }, this.interval);
    }

    cleanup() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
    }
    disconnectedCallback() { this.cleanup(); }
  }

  if (!customElements.get('midtf-benefit-marquee')) customElements.define('midtf-benefit-marquee', MidtfBenefitMarquee);
  if (!customElements.get('midtf-product-carousel')) customElements.define('midtf-product-carousel', MidtfProductCarousel);
})();
